/**
 * useTodayMetrics Hook
 *
 * React Query hook for fetching Today Page metrics.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchWithAuth } from '../../../../utils/api';
import { APIErrorException } from '../../../../services/types/errors';
import type { TodayMetricsResponse } from '../types/today.types';
import { API } from '../constants/today.constants';

// =============================================================================
// Query Keys
// =============================================================================

/**
 * Query key factory for today metrics queries
 */
export const todayKeys = {
  all: ['today'] as const,
  metrics: () => [...todayKeys.all, 'metrics'] as const,
  insight: () => [...todayKeys.all, 'insight'] as const,
};

// =============================================================================
// Hook Return Type
// =============================================================================

export interface UseTodayMetricsResult {
  data: TodayMetricsResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook to fetch Today Page metrics from the API.
 *
 * Features:
 * - Automatic refetching every 60 seconds
 * - 30 second stale time for caching
 * - Error handling with retry logic (no retry on 401)
 *
 * @example
 * ```tsx
 * const { data, isLoading, isError, refetch } = useTodayMetrics();
 *
 * if (isLoading) return <TodayPageSkeleton />;
 * if (isError) return <ErrorState />;
 *
 * return <TodayPage data={data} />;
 * ```
 */
export function useTodayMetrics(): UseTodayMetricsResult {
  const { data, isLoading, isError, error, refetch } = useQuery<
    TodayMetricsResponse,
    Error
  >({
    queryKey: todayKeys.metrics(),
    queryFn: async () => {
      return fetchWithAuth(API.ENDPOINT);
    },
    staleTime: API.STALE_TIME,
    refetchInterval: API.REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      // Don't retry on 401 Unauthorized
      if (error instanceof APIErrorException && error.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    data: data ?? null,
    isLoading,
    isError,
    error: error ?? null,
    refetch,
  };
}
